import {PageFragment, PageFragmentProps, PageFragmentState} from "../../shared/PageFragment";
import React, {ReactElement} from "react";
import {StudioAppTab, StudioAppTabsContainer} from "./StudioAppTabsContainer";
import {
  BackupOutlined,
  CategoryOutlined,
  LayersOutlined,
  PhotoOutlined,
  QrCodeOutlined,
  TextFormatOutlined,
  TextureOutlined,
  VideocamOutlined,
  WidgetsOutlined
} from "@mui/icons-material";
import {SidebarPosition, StudioSidebar} from "./StudioSidebar";
import {PAGE_HEIGHT, PAGE_WIDTH, StudioCanvas} from "./StudioCanvas";
import {
  CanvasElement,
  GroupCanvasElement,
  ImageCanvasElement,
  isGroupElement,
  ShapeCanvasElement,
  TextCanvasElement,
  UploadedPhoto
} from "./types";
import {PhotosPanel} from "./PhotosPanel";
import {BackgroundItem, BackgroundPanel} from "./BackgroundPanel";
import {ShapesPanel} from "./ShapesPanel";
import {LayersPanel} from "./LayersPanel";
import {StudioToolbar} from "./StudioToolbar";
import {Shape} from "./model/shapes/types";
import {TextPanel} from "./TextPanel";

enum EditorTab {
  LAYERS = "layers",
  TEMPLATES = "templates",
  PHOTOS = "photos",
  SHAPES = "shapes",
  VIDEOS = "videos",
  TEXT = "text",
  BACKGROUND = "background",
  QRCODE = "qrcode",
  UPLOAD = "upload",
}

const EDITOR_TABS: StudioAppTab[] = [
  {key: EditorTab.LAYERS, text: "Layers", iconType: LayersOutlined},
  {key: EditorTab.TEMPLATES, text: "Templates", iconType: WidgetsOutlined},
  {key: EditorTab.PHOTOS, text: "Photos", iconType: PhotoOutlined},
  {key: EditorTab.SHAPES, text: "Shapes", iconType: CategoryOutlined},
  {key: EditorTab.VIDEOS, text: "Videos", iconType: VideocamOutlined, disabled: true},
  {key: EditorTab.TEXT, text: "Text", iconType: TextFormatOutlined},
  {key: EditorTab.BACKGROUND, text: "Background", iconType: TextureOutlined},
  {key: EditorTab.QRCODE, text: "QR Code", iconType: QrCodeOutlined, disabled: true},
  {key: EditorTab.UPLOAD, text: "Upload", iconType: BackupOutlined, disabled: true},
];

const EDITOR_TAB_TITLES: Partial<Record<EditorTab, string>> = {
  [EditorTab.LAYERS]: "Layers",
  [EditorTab.TEMPLATES]: "Templates",
  [EditorTab.PHOTOS]: "Photos",
  [EditorTab.SHAPES]: "Shapes",
  [EditorTab.VIDEOS]: "Videos",
  [EditorTab.TEXT]: "Text",
  [EditorTab.BACKGROUND]: "Background",
  [EditorTab.QRCODE]: "QR Code",
  [EditorTab.UPLOAD]: "Upload",
};

export type StudioFragmentProps = PageFragmentProps & {}

type StudioFragmentState = PageFragmentState & {
  editorTab: EditorTab;
  elements: CanvasElement[];
  selectedIds: string[];
  uploadedPhotos: UploadedPhoto[];
}

export class StudioFragment extends PageFragment<StudioFragmentProps, StudioFragmentState> {

  protected onCreateState(props?: StudioFragmentProps): StudioFragmentState {
    return {
      ...super.onCreateState(props),
      editorTab: EditorTab.LAYERS,
      elements: [],
      selectedIds: [],
      uploadedPhotos: [],
    };
  }

  renderContent(): ReactElement {
    return <StudioAppTabsContainer
      tabs={EDITOR_TABS}
      initialSelectedKey={this.state.editorTab}
      onSelect={key => this.setState({editorTab: key as EditorTab})}>
      {this.renderEditorTab()}
    </StudioAppTabsContainer>;
  }

  private renderEditorTab(): ReactElement {
    const {editorTab, elements, selectedIds} = this.state;
    const selectedElement = selectedIds.length === 1 ? elements.find(el => el.id === selectedIds[0]) || null : null;
    return (
      <div style={{flexGrow: 1, display: "flex"}}>
        <StudioSidebar
          sidebarCollapsible
          title={EDITOR_TAB_TITLES[editorTab] || ""}
          position={SidebarPosition.LEFT}
        >
          {this.renderSidebarContent()}
        </StudioSidebar>
        <div style={{flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden"}}>
          <StudioToolbar
            selectedElement={selectedElement}
            selectedCount={selectedIds.length}
            onUndo={() => {}}
            onRedo={() => {}}
            onDuplicate={() => {
              if (!selectedElement) return;
              const id = `${selectedElement.type}_${Date.now()}`;
              const clone = {...selectedElement, id, name: `${selectedElement.name}-copy`, x: selectedElement.x + 20, y: selectedElement.y + 20};
              this.setState(prev => ({
                elements: [...prev.elements, clone],
                selectedIds: [id],
              }));
            }}
            onDelete={() => {
              if (selectedIds.length === 0) return;
              this.setState(prev => ({
                elements: prev.elements.filter(el => !prev.selectedIds.includes(el.id)),
                selectedIds: [],
              }));
            }}
            onGroup={() => {
              if (selectedIds.length < 2) return;
              this.setState(prev => {
                const toGroup = prev.elements.filter(el => prev.selectedIds.includes(el.id));
                if (toGroup.length < 2) return null;
                // Compute bounding box of selected elements
                const minX = Math.min(...toGroup.map(el => el.x));
                const minY = Math.min(...toGroup.map(el => el.y));
                const maxX = Math.max(...toGroup.map(el => el.x + el.width));
                const maxY = Math.max(...toGroup.map(el => el.y + el.height));
                // Make children positions relative to group origin
                const children = toGroup.map(el => ({
                  ...el,
                  x: el.x - minX,
                  y: el.y - minY,
                }));
                const groupId = `group_${Date.now()}`;
                const group: GroupCanvasElement = {
                  id: groupId,
                  name: this.nextElementName("group"),
                  type: "group",
                  x: minX,
                  y: minY,
                  width: maxX - minX,
                  height: maxY - minY,
                  rotation: 0,
                  opacity: 1,
                  visible: true,
                  locked: false,
                  children,
                };
                // Insert group at the position of the topmost selected element
                const remaining = prev.elements.filter(el => !prev.selectedIds.includes(el.id));
                const lastSelectedIdx = Math.max(...toGroup.map(el => prev.elements.indexOf(el)));
                const insertIdx = remaining.findIndex((_, i) => {
                  // Find how many original elements before this remaining element were after lastSelectedIdx
                  const origIdx = prev.elements.indexOf(remaining[i]);
                  return origIdx > lastSelectedIdx;
                });
                const newElements = [...remaining];
                if (insertIdx === -1) {
                  newElements.push(group);
                } else {
                  newElements.splice(insertIdx, 0, group);
                }
                return {elements: newElements, selectedIds: [groupId]};
              });
            }}
            onUngroup={() => {
              if (!selectedElement || !isGroupElement(selectedElement)) return;
              this.setState(prev => {
                const groupEl = selectedElement as GroupCanvasElement;
                // Restore children to absolute positions
                const restoredChildren = groupEl.children.map(child => ({
                  ...child,
                  x: child.x + groupEl.x,
                  y: child.y + groupEl.y,
                }));
                const groupIdx = prev.elements.indexOf(prev.elements.find(el => el.id === groupEl.id)!);
                const newElements = [...prev.elements];
                newElements.splice(groupIdx, 1, ...restoredChildren);
                return {
                  elements: newElements,
                  selectedIds: restoredChildren.map(c => c.id),
                };
              });
            }}
          />
          <StudioCanvas
            elements={elements}
            selectedIds={selectedIds}
            onSelect={ids => this.setState({selectedIds: ids})}
            onElementUpdate={(id, x, y, width, height, rotation) => {
              this.setState(prev => ({
                elements: prev.elements.map(el =>
                  el.id === id ? {...el, x, y, width, height, rotation} : el
                ),
              }));
            }}
            onElementTextUpdate={(id, text) => {
              this.setState(prev => ({
                elements: prev.elements.map(el =>
                  el.id === id ? {...el, text} : el
                ),
              }));
            }}
          />
        </div>
      </div>
    );
  }

  private nextElementName(prefix: string): string {
    const existing = this.state.elements.filter(el => el.name.startsWith(prefix));
    return `${prefix}-${existing.length + 1}`;
  }

  private renderSidebarContent(): ReactElement {
    const {elements, selectedIds} = this.state;
    switch (this.state.editorTab) {
      case EditorTab.LAYERS:
        return (
          <LayersPanel
            elements={elements}
            selectedIds={selectedIds}
            onSelect={(id) => this.setState({selectedIds: [id]})}
            onRename={(id, name) => {
              this.setState(prev => ({
                elements: prev.elements.map(el => el.id === id ? {...el, name} : el),
              }));
            }}
            onToggleVisibility={(id) => {
              this.setState(prev => ({
                elements: prev.elements.map(el => el.id === id ? {...el, visible: !el.visible} : el),
                selectedIds: prev.selectedIds.filter(sid => sid !== id),
              }));
            }}
            onToggleLock={(id) => {
              this.setState(prev => ({
                elements: prev.elements.map(el => el.id === id ? {...el, locked: !el.locked} : el),
              }));
            }}
            onDelete={(id) => {
              this.setState(prev => ({
                elements: prev.elements.filter(el => el.id !== id),
                selectedIds: prev.selectedIds.filter(sid => sid !== id),
              }));
            }}
            onReorder={(reordered) => this.setState({elements: reordered})}
          />
        );
      case EditorTab.PHOTOS:
        return (
          <PhotosPanel
            photos={this.state.uploadedPhotos}
            onPhotoUploaded={(photo) => {
              this.setState({
                uploadedPhotos: [...this.state.uploadedPhotos, photo],
              });
            }}
            onPhotoAdd={(photo) => {
              const id = `img_${Date.now()}`;
              const aspectRatio = photo.htmlImage.naturalWidth / photo.htmlImage.naturalHeight;
              const width = Math.min(PAGE_WIDTH * 0.5, photo.htmlImage.naturalWidth);
              const height = width / aspectRatio;
              const element: ImageCanvasElement = {
                id,
                name: this.nextElementName("image"),
                type: "image",
                x: (PAGE_WIDTH - width) / 2,
                y: (PAGE_HEIGHT - height) / 2,
                width,
                height,
                rotation: 0,
                opacity: 1,
                visible: true,
                locked: false,
                src: photo.src,
                htmlImage: photo.htmlImage,
              };
              this.setState(prev => ({
                elements: [...prev.elements, element],
                selectedIds: [id],
              }));
            }}
          />
        );
      case EditorTab.SHAPES:
        return (
          <ShapesPanel
            onShapeAdd={(shape: Shape) => {
              const id = `shape_${Date.now()}`;
              const size = 150;
              const element: ShapeCanvasElement = {
                id,
                name: this.nextElementName(shape),
                type: "shape",
                shape: shape,
                fill: "#aaaaaa",
                x: (PAGE_WIDTH - size) / 2,
                y: (PAGE_HEIGHT - size) / 2,
                width: size,
                height: size,
                rotation: 0,
                opacity: 1,
                visible: true,
                locked: false,
              };
              this.setState(prev => ({
                elements: [...prev.elements, element],
                selectedIds: [id],
              }));
            }}
          />
        );
      case EditorTab.TEXT:
        return (
          <TextPanel
            onTextAdd={(text, fontSize, fontStyle) => {
              const id = `text_${Date.now()}`;
              const width = PAGE_WIDTH * 0.6;
              const height = fontSize * 1.5;
              const element: TextCanvasElement = {
                id,
                name: this.nextElementName("text"),
                type: "text",
                x: (PAGE_WIDTH - width) / 2,
                y: (PAGE_HEIGHT - height) / 2,
                width,
                height,
                rotation: 0,
                opacity: 1,
                visible: true,
                locked: false,
                text,
                fontSize,
                fontFamily: "Arial",
                fill: "#000000",
                align: "center",
                fontStyle,
              };
              this.setState(prev => ({
                elements: [...prev.elements, element],
                selectedIds: [id],
              }));
            }}
          />
        );
      case EditorTab.BACKGROUND:
        return (
          <BackgroundPanel
            onBackgroundSelect={(item: BackgroundItem) => {
              const id = `bg_${Date.now()}`;
              const element: ImageCanvasElement = {
                id,
                name: this.nextElementName("background"),
                type: "image",
                x: 0,
                y: 0,
                width: PAGE_WIDTH,
                height: PAGE_HEIGHT,
                rotation: 0,
                opacity: 1,
                visible: true,
                locked: false,
                src: item.src,
                htmlImage: item.htmlImage,
              };
              this.setState(prev => {
                // Remove any existing background element
                const filtered = prev.elements.filter(el => !el.id.startsWith("bg_"));
                return {
                  elements: [element, ...filtered],
                  selectedIds: [id],
                };
              });
            }}
          />
        );
      default:
        return <div/>;
    }
  }
}
